import { useState, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import TableSkeleton from '../../../utils/common/components/TableSkeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import {
  StyledTableCell,
  StyledTableRow,
  StyledTableContainer,
  StyledPaper,
  PrimaryButton,
  UploadStatusBox,
} from '../../../utils/common/styles/commonStyles';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { listUploadHistory, getUploadProgressMultiple } from '../store/mile.actions';
import {
  uploadHistoryData,
  uploadHistoryStatus,
  uploadHistoryError,
} from '../store/mile.selectors';
import { selectAuthUser } from '../../auth/store/auth.selectors';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { Refresh } from '@mui/icons-material';

const ResponsiveTableCell = styled(StyledTableCell)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    [`&.${tableCellClasses.body}`]: {
      '&:first-of-type': {
        paddingLeft: 5,
      },
    },
  },
}));

const columns = [
  { dataKey: 'fileName', label: 'File Name', width: 250, sortable: true },
  { dataKey: 'status', label: 'Status', width: 120, sortable: true },
  { dataKey: 'uploadedBy', label: 'Uploaded By', width: 180, sortable: true },
  { dataKey: 'uploadedOn', label: 'Uploaded On', width: 150, sortable: true },
  { dataKey: 'uploadProgress', label: 'Progress', width: 150, sortable: false },
  { dataKey: 'cloudUrl', label: 'Download', width: 100, sortable: false },
];

const UploadHistoryTable = ({ refreshTrigger, onOpenUpload }) => {
  const dispatch = useDispatch();

  const uploadHistoryFromRedux = useSelector(uploadHistoryData);
  const status = useSelector(uploadHistoryStatus);
  const error = useSelector(uploadHistoryError);
  const user = useSelector(selectAuthUser);

  const [uploadHistory, setUploadHistory] = useState(uploadHistoryFromRedux);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('uploadedOn');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshingIds, setRefreshingIds] = useState(new Set());

  const companyid = JSON.parse(localStorage.getItem('user')).companyid;
  const isUploadAllowed = user?.rolename.includes('Rieker') || user?.rolename === 'Manager';

  useEffect(() => {
    dispatch(listUploadHistory(companyid));
  }, [companyid, refreshTrigger]);

  useEffect(() => {
    setUploadHistory(uploadHistoryFromRedux);
  }, [uploadHistoryFromRedux]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleRefreshProgress = async () => {
    setIsRefreshing(true);

    try {
      const incompletedUploads = uploadHistory.filter((item) => (item.uploadProgress || 0) < 100);

      if (incompletedUploads.length > 0) {
        const uploadHistoryIds = incompletedUploads
          .map((item) => item.uploadHistoryID || item.uploadHistoryId || item.id)
          .filter((id) => id);
        // Mark these IDs as refreshing
        setRefreshingIds(new Set(uploadHistoryIds));
        if (uploadHistoryIds.length > 0) {
          const responseData = await dispatch(getUploadProgressMultiple(uploadHistoryIds));
          const progressData = Array.isArray(responseData)
            ? responseData
            : responseData?.data || [];
          // Create a map for easier lookup and to preserve existing progress if needed
          const progressMap = {};
          const statusMap = {};
          progressData.forEach((item) => {
            const key = item.uploadHistoryID || item.uploadHistoryId || item.id;
            const progress = item.progress !== undefined ? item.progress : item.uploadProgress;
            progressMap[key] = progress;
            if (item.status) {
              statusMap[key] = item.status;
            }
          });
          setUploadHistory((prevHistory) =>
            prevHistory.map((item) => {
              const itemId = item.uploadHistoryID || item.uploadHistoryId || item.id;
              const apiProgress = progressMap[itemId];
              const apiStatus = statusMap[itemId];
              if (apiProgress !== undefined && apiProgress !== null) {
                const updatedItem = {
                  ...item,
                  uploadProgress: apiProgress,
                  status: apiProgress === 100 ? 'FINISHED' : apiStatus || item.status,
                };
                // Update status if available from API
                return updatedItem;
              }
              return item;
            })
          );
        }
      }
    } catch (err) {
      console.error('Error fetching upload progress:', err.response?.data || err.message);
    } finally {
      setIsRefreshing(false);
      setRefreshingIds(new Set());
    }
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    // Handle date comparison
    if (orderBy === 'uploadedOn') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
    return 0;
  };

  const sortedHistory = useMemo(() => {
    if (!orderBy) return uploadHistory;
    return [...uploadHistory].sort(getComparator(order, orderBy));
  }, [uploadHistory, order, orderBy]);

  const getCellContent = (column, row) => {
    if (column.dataKey === 'status') {
      return (
        <UploadStatusBox status={row.status}>
          <Typography>{row.status}</Typography>
        </UploadStatusBox>
      );
    }

    if (column.dataKey === 'uploadedOn') {
      const date = new Date(row[column.dataKey]);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }

    if (column.dataKey === 'uploadProgress') {
      const progress = row[column.dataKey] || 0;
      const itemId = row.uploadHistoryID || row.uploadHistoryId || row.id;
      // Show skeleton if currently refreshing this item
      if (refreshingIds.has(itemId)) {
        return <Skeleton variant="text" sx={{ fontSize: '1rem' }} />;
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: '100%', mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor:
                    progress === 100 ? '#22c55e' : progress > 50 ? '#3b82f6' : '#f59e0b',
                },
              }}
            />
          </Box>
          <Box sx={{ minWidth: 45 }}>
            <Typography variant="body2" color="text.secondary">
              {`${progress}%`}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (column.dataKey === 'cloudUrl') {
      return row[column.dataKey] ? (
        <Link
          href={row[column.dataKey]}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            '&:hover': {
              textDecoration: 'none',
              cursor: 'pointer',
            },
          }}
        >
          <CloudDownloadOutlinedIcon sx={{ color: 'primary.rieker' }} />
        </Link>
      ) : (
        <Typography variant="body2" color="text.disabled">
          -
        </Typography>
      );
    }

    if (column.dataKey === 'fileName') {
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: '#1e293b',
          }}
        >
          {row[column.dataKey]}
        </Typography>
      );
    }

    return row[column.dataKey];
  };

  if (status === 'loading') {
    return <TableSkeleton />;
  }

  if (status === 'fail') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Upload History
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <PrimaryButton
            size="small"
            startIcon={<Refresh />}
            aria-label="refresh all"
            disabled={isRefreshing}
            onClick={handleRefreshProgress}
            title="Refresh upload progress for all incomplete uploads"
          >
            {' '}
            Refresh Progress
          </PrimaryButton>
          {isUploadAllowed && (
            <PrimaryButton
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={onOpenUpload}
              size="small"
            >
              Upload File
            </PrimaryButton>
          )}
        </Box>
      </Box>

      <StyledPaper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <StyledTableContainer sx={{ flex: 1 }}>
          <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <ResponsiveTableCell
                    key={column.dataKey}
                    align="left"
                    style={{ width: column.width }}
                    sortDirection={orderBy === column.dataKey ? order : false}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.dataKey}
                        direction={orderBy === column.dataKey ? order : 'asc'}
                        onClick={() => handleRequestSort(column.dataKey)}
                      >
                        {column.label}
                        {orderBy === column.dataKey ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </ResponsiveTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHistory?.length > 0 ? (
                sortedHistory.map((row, index) => (
                  <StyledTableRow key={row.id || index}>
                    {columns.map((column) => (
                      <ResponsiveTableCell
                        key={`${row.id}-${column.dataKey}`}
                        align={column.dataKey === 'cloudUrl' ? 'center' : 'left'}
                      >
                        {getCellContent(column, row)}
                      </ResponsiveTableCell>
                    ))}
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <ResponsiveTableCell colSpan={columns.length} align="center">
                    <Box
                      sx={{
                        py: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 64, color: '#cbd5e1' }} />
                      <Typography variant="body1" color="text.secondary">
                        No upload history available
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        Upload your first file to get started
                      </Typography>
                    </Box>
                  </ResponsiveTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </StyledPaper>
    </Box>
  );
};

export default UploadHistoryTable;

UploadHistoryTable.propTypes = {
  refreshTrigger: PropTypes.number,
  onOpenUpload: PropTypes.func,
};
